import {
  useIsMutating,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Api } from "../../service/Api";
import { emptyDataMap } from "../../service/resources/data-map/dataMapHelpers";
import * as Mutators from "../../service/resources/data-map/dataMapMutators";
import { updateQueryData, useApiSnackbar } from "../../service/resources/mutators";
import { DataMapDto } from "../../service/server/dto/datamap/DataMapDto";
import { PatchDataMapRequest } from "../../service/server/dto/datamap/DataMapRequestDto";
import { UseApiData } from "../api";
import { useDataRequest } from "../api";

import { ApiJsonPatch, OrganizationPublicId } from "../../service/server/Types";

export type UseDataMap = UseApiData<DataMapDto> & {
  mutators: {
    patch: DataMapPatchMutation;
  };
};

export const useDataMap = (orgId: OrganizationPublicId): UseDataMap => {
  const api = useCallback(() => Api.dataMap.getDataMap(orgId), [orgId]);

  const { result, refresh, request, invalidate } = useDataRequest({
    api,
    queryKey: queryKeyDataMap(orgId),
  });

  const patch = usePatchDataMap(orgId);

  const v2DataMap = useMemo(() => {
    return result ?? emptyDataMap();
  }, [result]);

  return {
    data: v2DataMap,
    request,
    refresh,
    invalidate,
    remote: result,
    mutators: {
      patch,
    },
  };
};

const queryKeyDataMap = (orgId: OrganizationPublicId) => ["dataMap", orgId];
export const mutationKeyPatchDataMap = (orgId: OrganizationPublicId) => ["dataMap", orgId, "patch"];

export type DataMapPatchMutation = UseMutationResult<
  ApiJsonPatch,
  unknown,
  PatchDataMapRequest,
  unknown
>;
const usePatchDataMap = (orgId: OrganizationPublicId): DataMapPatchMutation => {
  const qc = useQueryClient();
  const { apiError } = useApiSnackbar();

  const mutation = useMutation(
    async (update: PatchDataMapRequest) => Api.dataMap.patchDataMap(orgId, update),

    {
      mutationKey: mutationKeyPatchDataMap(orgId),
      onMutate: (update) => {
        updateQueryData<DataMapDto>(qc, queryKeyDataMap(orgId), (map) => {
          Mutators.addPendingOperations(update.operations, map);
        });
      },

      onSuccess: (response, update) => {
        updateQueryData<DataMapDto>(qc, queryKeyDataMap(orgId), (map) => {
          Object.keys(response).forEach((key) => {
            map[key] = response[key];
          });
          Mutators.removePendingOperations(update.operations, map);
        });
      },

      onError: (_error, update) => {
        updateQueryData<DataMapDto>(qc, queryKeyDataMap(orgId), (map) => {
          Mutators.removePendingOperations(update.operations, map);
        });
        apiError(
          "Something went wrong while we were updating the data map. Please try again later.",
        );
      },
    },
  );

  const running = useIsMutating({ mutationKey: mutationKeyPatchDataMap(orgId) });
  const cloned = useMemo(
    () => ({ ...mutation, isLoading: mutation.isLoading || running > 0 }),
    [mutation, running],
  );

  // @ts-ignore
  return cloned;
};
